import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiClient } from "@/common/apiClient";
import { toast } from "sonner";

interface KPIForm {
  _id: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
}

export const ViewAllKPIForm = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState<KPIForm[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchForms = async () => {
      try {
        const response = await apiClient.get("/api/kpi-form");
        if (response.success) {
          setForms(response.data || []);
        } else {
          toast.error("Failed to fetch KPI forms");
        }
      } catch (error) {
        toast.error("Failed to fetch KPI forms: " + (error as any).message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchForms();
  }, []);

  const handleStatusChange = async (formId: string, newStatus: string) => {
    try {
      const response = await apiClient.put(`/api/kpi-form/${formId}`, {
        status: newStatus,
      });
      console.log(response);
      if (response.success) {
        setForms(
          forms.map((f) => (f._id === formId ? { ...f, status: newStatus } : f))
        );
        toast.success(`Status updated to ${newStatus}`);
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">KPI Forms</h1>
        <Button onClick={() => navigate("/create-kpi-form")}>
          Create New Form
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created At</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {forms.map((form) => (
            <TableRow key={form._id}>
              <TableCell>{form.title}</TableCell>
              <TableCell>{form.description || "-"}</TableCell>
              <TableCell>
                <Select
                  value={form.status}
                  onValueChange={(value) => handleStatusChange(form._id, value)}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                {new Date(form.createdAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/update-kpi-form/${form._id}`)}
                >
                  Edit
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
